import { bus } from "@App/domain/bus";
import { EventType, type ResultViewedEvent } from "@App/domain/events";
import type { Brand, Garage, Generation, Motorization, PremiumService, Service } from "@App/types";
import type { SortOrderType } from "@App/types/enums";
import { Origin } from "@App/utils/analytics/origins";
import { useEffect, useRef } from "react";

// ---------------------------
// Publish analytics event
// ---------------------------
export function onResultsLoaded(props: {
  distanceClosestServicePartner?: number;
  serviceRadius: number;
  earliestAppointment: string | null;
  servicePartnerCount: number;
  loadingTime: number;
  vehicleDescription?: string;
  vehicleModel: string | null;
  brand: string | null;
  serviceSlug: string | null;
  serviceId: string | null;
  serviceName: string | null;
  organizationId: number;
  organizationName?: string;
  sortMethod: SortOrderType;
  vehicleEngine: string | null;
  premiumServices: string[];
}) {
  const event: ResultViewedEvent = {
    type: EventType.RESULT_VIEWED,
    origin: Origin.WLV1,
    organization_id: props.organizationId,
    organization_name: props.organizationName,
    service_partner_count: props.servicePartnerCount,
    sort_method: props.sortMethod,
    service_slug: props.serviceSlug,
    service_id: props.serviceId,
    service_name: props.serviceName,
    service_radius: props.serviceRadius,
    vehicle_brand: props.brand,
    vehicle_engine: props.vehicleEngine,
    vehicle_model: props.vehicleModel,
    loading_time: props.loadingTime,
    distance_closest_service_partner: props.distanceClosestServicePartner,
    earliest_appointment: props.earliestAppointment,
    premium_services: props.premiumServices,
  };

  bus.publish(event);
}

// ---------------------------
// Props for the custom hook
// ---------------------------
interface UseResultsAnalyticsParams {
  loading: boolean;
  garages: Garage[] | null;
  loadingStartTime: number | null;
  distance: number;
  generation: Generation | null;
  brand: Brand | null;
  service: Service | null;
  organization: { id: number; name?: string };
  sortBy: SortOrderType;
  motorization: Motorization | null;
  premiumServices: PremiumService[];
}

// ---------------------------
// Hook with injectable Worker (ES2020-compatible)
// ---------------------------
export function useResultsAnalytics(
  props: UseResultsAnalyticsParams,
  WorkerClass: typeof Worker = Worker, // Inject Worker for tests
) {
  const {
    loading,
    garages,
    loadingStartTime,
    distance,
    generation,
    brand,
    service,
    organization,
    sortBy,
    motorization,
    premiumServices,
  } = props;

  const hasSentEvent = useRef(false);

  // Instead of using import.meta.url
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    if (!workerRef.current) {
      workerRef.current = new WorkerClass("/js/worker/analyticsWorker.js", {
        type: "module",
      });
    }
  }, [WorkerClass]);

  useEffect(() => {
    if (loading) hasSentEvent.current = false;
  }, [loading]);

  useEffect(() => {
    if (loading || hasSentEvent.current || !garages?.length || !loadingStartTime) return;
    hasSentEvent.current = true;

    const loadingTime = Date.now() - loadingStartTime;

    const worker = workerRef.current;

    worker.postMessage({ garages, premiumServices });

    const handleMessage = (event: MessageEvent) => {
      const { distanceClosestServicePartner, earliestAppointment, premiumServiceNames } =
        event.data;

      onResultsLoaded({
        distanceClosestServicePartner,
        serviceRadius: distance,
        earliestAppointment: earliestAppointment || null,
        servicePartnerCount: garages.length,
        loadingTime,
        vehicleModel: generation?.name || null,
        brand: brand?.name || null,
        serviceSlug: service?.slug || null,
        serviceId: service?.id ? String(service.id) : null,
        serviceName: service?.name || null,
        organizationId: organization.id,
        organizationName: organization.name,
        sortMethod: sortBy,
        vehicleEngine: motorization?.name || null,
        premiumServices: premiumServiceNames,
      });
    };

    worker.addEventListener("message", handleMessage);

    return () => {
      worker.removeEventListener("message", handleMessage);
    };
  }, [
    loading,
    garages,
    loadingStartTime,
    distance,
    generation,
    brand,
    service,
    organization,
    sortBy,
    motorization,
    premiumServices,
  ]);
}
